import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import api from '@/api/client';

interface User {
  id: string;
  email: string;
  /** Display name from the users table — present on login/register responses. */
  name?: string;
  role: string;
  tenantId: string;
  isPlatformAdmin?: boolean;
  impersonating?: boolean;
  /** True for invited members still on their temp password — the app
   * routes them to /set-password until they change it. */
  mustChangePassword?: boolean;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True while running under an admin impersonation session. */
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Swap this session for a 1h impersonation token minted by /api/admin.
   * The admin's own session is parked in localStorage for the return trip. */
  impersonate: (token: string, tenant: { id: string; name: string }) => void;
  /** Restore the parked admin session and go back to /admin. */
  endImpersonation: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('auth_token'),
  );
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('auth_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!user;
  const isImpersonating = !!localStorage.getItem('admin_token');

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_tenant_id');
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; user: User }>('/auth/login', {
      email,
      password,
    });
    const { token: newToken, user: newUser } = res.data;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
    localStorage.setItem('tenant_id', newUser.tenantId);
  }, []);

  const impersonate = useCallback(
    (impersonationToken: string, tenant: { id: string; name: string }) => {
      // Park the admin session for the return trip.
      const currentToken = localStorage.getItem('auth_token');
      const currentUser = localStorage.getItem('auth_user');
      const currentTenant = localStorage.getItem('tenant_id');
      if (currentToken) localStorage.setItem('admin_token', currentToken);
      if (currentUser) localStorage.setItem('admin_user', currentUser);
      if (currentTenant) localStorage.setItem('admin_tenant_id', currentTenant);

      localStorage.setItem('auth_token', impersonationToken);
      localStorage.setItem('tenant_id', tenant.id);
      localStorage.removeItem('auth_user'); // repopulated by /auth/me on reload
      // Hard navigation: forces /auth/me revalidation and drops all cached
      // tenant state (TenantContext, SWR-ish hooks) in one stroke.
      window.location.href = '/';
    },
    [],
  );

  const endImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    const adminTenant = localStorage.getItem('admin_tenant_id');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_tenant_id');
    if (adminToken) {
      localStorage.setItem('auth_token', adminToken);
      if (adminUser) localStorage.setItem('auth_user', adminUser);
      if (adminTenant) localStorage.setItem('tenant_id', adminTenant);
      window.location.href = '/admin';
    } else {
      // Parked session lost — fail safe to a clean login.
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('tenant_id');
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await api.get<{ user: User }>('/auth/me');
        setUser(res.data.user);
        localStorage.setItem('auth_user', JSON.stringify(res.data.user));
      } catch {
        logout();
      } finally {
        setIsLoading(false);
      }
    }
    validateToken();
  }, [token, logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated,
        isLoading,
        isImpersonating,
        login,
        logout,
        impersonate,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { AuthProvider, useAuth };
export default AuthContext;
